import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField } from "@mui/material";
import { toast } from "react-toastify";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import useModal from "../../hooks/useModal";
import Button from "../ui/Button";
import { scheduleRoundTable } from "../../services/exitManagement";
import { formatDateToLocalISOString } from "../../Helper/functionHelper";

// Define schema
const remarksSchema = z.object({
  remarks: z.string().min(1, { message: "Remarks are required" }),
  datetime: z
    .union([z.date(), z.null()])
    .refine((val) => val instanceof Date && !isNaN(val.getTime()), {
      message: "Date and time is required",
    })
    .refine((val) => val > new Date(), {
      message: "Date and time must be in the future",
    }),
});

const ScheduleRoundTable = ({ resignationId, fetchResignations }) => {
  const { hideModal } = useModal();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(remarksSchema),
    defaultValues: {
      remarks: "",
      datetime: null, // Default value for datetime
    },
  });

  const hanldeScheduleRoundTableSubmit = async (data, resignationId) => {
    try {
      const payload = {
        ...data,
        datetime: formatDateToLocalISOString(data.datetime),
      };
      const response = await scheduleRoundTable(resignationId, payload);
      if (response.status === 200) {
        toast.success(
          response.data.message || "Resignation processed successfully"
        );
        reset();
        hideModal();
        fetchResignations();
      } else {
        toast.error(response.data.message || "Failed to process resignation");
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error(error.response.data.message || "Bad Request: Invalid data");
      } else {
        console.error("Error submitting resignation:", error);
        toast.error("An unexpected error occurred.");
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit((data) =>
        hanldeScheduleRoundTableSubmit(data, resignationId)
      )}
      className="pt-2"
    >
      <div className="row mb-3">
        <div className="col-md-12">
          <Controller
            name="datetime"
            control={control}
            render={({ field }) => (
              <DateTimePicker
                {...field}
                format="dd MMM yyyy hh:mm a"
                label="Choose Date and Time*"
                minDateTime={new Date()}
                disabled={isSubmitting}
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                    error: !!errors.datetime,
                    helperText: errors.datetime?.message,
                  },
                }}
              />
            )}
          />
        </div>
        <div className="col-md-12 mt-2">
          <Controller
            name="remarks"
            control={control}
            render={({ field }) => (
              <TextField
                label="Remarks*"
                variant="standard"
                {...field}
                multiline
                rows={4}
                error={!!errors.remarks}
                helperText={errors.remarks?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
        </div>
      </div>
      <div className="d-flex justify-content-end gap-1">
        <Button
          variant="success"
          size="small"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
        <Button
          variant="light"
          size="small"
          onClick={hideModal}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default ScheduleRoundTable;
